'use client'

import React, { useEffect, useRef, useState } from 'react'
import styles from './asset-360-viewer.module.scss'
import { cn } from '@/lib/utils'

export interface Asset360ViewerProps {
  className?: string
  sprite: string
  frames: number
  frameWidth: number
  frameHeight: number
  friction?: number
  reverse?: boolean
}

const Asset360Viewer: React.FC<Asset360ViewerProps> = ({
  className,
  sprite,
  frames,
  frameWidth,
  frameHeight,
  friction = 1,
  reverse = false,
}) => {
  const [currentFrame, setCurrentFrame] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [offset, setOffset] = useState(0)
  const viewerRef = useRef<HTMLDivElement | null>(null)

  const totalFrames = frames

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (event: MouseEvent) => {
        const moveX = event.clientX - startX
        const newFrame = (offset + (moveX / friction) * (reverse ? -1 : 1)) % totalFrames
        setCurrentFrame(Math.floor(newFrame < 0 ? totalFrames + newFrame : newFrame))
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        setOffset(currentFrame)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, startX, offset, currentFrame, friction, totalFrames, reverse])

  const handleMouseDown = (event: React.MouseEvent) => {
    setIsDragging(true)
    setStartX(event.clientX)
  }

  const getBackgroundPosition = (): string => {
    const xPosition = Math.round(-currentFrame * frameWidth)

    return `${xPosition}px 0px`
  }

  return (
    <div
      className={cn(styles.viewer, className)}
      ref={viewerRef}
      onMouseDown={handleMouseDown}
      style={{
        width: `${frameWidth}px`,
        height: `${frameHeight}px`,
        backgroundImage: `url(${sprite})`,
        backgroundPosition: getBackgroundPosition(),
        backgroundSize: `${frameWidth * frames}px ${frameHeight}px`,
      }}
    />
  )
}

export default Asset360Viewer
